import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { families, TERMS } from '../data/rentalCatalogue';
import Brand from './Brand';
import './Header.css';

const navigation = [
  { label: 'Daily Van Hire', path: '/van-rental', children: [
    ...TERMS.rental.map(term => ({ label: term.label, path: `/van-rental?term=${term.value}` })),
    { label: 'Extend your hire', path: '/extend-your-hire' },
  ] },
  { label: 'Business Hire', path: '/van-hire', children: TERMS.hire.map(term => ({ label: term.label, path: `/van-hire?term=${term.value}` })) },
  { label: 'Our Vans', path: '/our-vans', children: families.map(family => ({ label: `Ford ${family.label}`, path: `/our-vans/ford/${family.id}` })) },
  { label: 'Vehicle Sales', path: '/vehicle-sales', children: [
    { label: 'Current van stock', path: '/van-search' },
    { label: 'Used vans', path: '/vans/used' },
    { label: 'New vans', path: '/vans/new' },
  ] },
  { label: 'Fleet Solutions', path: '/business-van-hire', children: [
    { label: 'Business hire options', path: '/van-hire' },
    { label: 'Request a fleet quote', path: '/request-a-quote?service=hire' },
    { label: 'Talk to our team', path: '/contact' },
  ] },
  { label: 'Electric & Hybrid', path: '/electric-hybrid-vans', children: [
    { label: 'Electric vans', path: '/electric-hybrid-vans?powertrain=electric' },
    { label: 'Plug-in hybrid vans', path: '/electric-hybrid-vans?powertrain=phev' },
  ] },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const mobileQuery = '(max-width: 1199px)';

function PhoneIcon() {
  return <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="m7 3 3 5-2 2a17 17 0 0 0 6 6l2-2 5 3-1 3c-.3.8-1.1 1.2-2 1C10 20 4 14 3 6c-.2-.9.2-1.7 1-2z" /></svg>;
}

function Header() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia?.(mobileQuery).matches ?? false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const header = useRef(null);
  const menuButton = useRef(null);
  const dropdownButtons = useRef({});
  const dropdownLists = useRef({});
  const focusDropdown = useRef(false);
  const location = useLocation();

  function closeNavigation() {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  }

  function handleKeyDown(event) {
    if (event.key !== 'Escape' || (!mobileMenuOpen && !openDropdown)) return;
    event.preventDefault();
    event.stopPropagation();
    if (openDropdown) {
      setOpenDropdown(null);
      dropdownButtons.current[openDropdown]?.focus();
    } else {
      setMobileMenuOpen(false);
      menuButton.current?.focus();
    }
  }

  useEffect(() => {
    const media = window.matchMedia?.(mobileQuery);
    if (!media) return undefined;
    const updateLayout = (event) => {
      setIsMobile(event.matches);
      setOpenDropdown(null);
      setMobileMenuOpen(false);
    };
    media.addEventListener('change', updateLayout);
    return () => media.removeEventListener('change', updateLayout);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (openDropdown && focusDropdown.current) {
      dropdownLists.current[openDropdown]?.querySelector('a')?.focus();
      focusDropdown.current = false;
    }
  }, [openDropdown]);

  useEffect(() => {
    if (!mobileMenuOpen && !openDropdown) return undefined;
    const closeOutside = (event) => {
      if (openDropdown && !dropdownButtons.current[openDropdown]?.contains(event.target) && !dropdownLists.current[openDropdown]?.contains(event.target)) setOpenDropdown(null);
      if (!header.current?.contains(event.target)) closeNavigation();
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [mobileMenuOpen, openDropdown]);

  return (
    <header className="vanir-header" ref={header} onKeyDown={handleKeyDown} onBlur={(event) => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) closeNavigation();
    }}>
      <div className="vanir-header__main">
        <Link to="/" className="vanir-header__logo" aria-label="Vanir Motor Group" onClick={closeNavigation}>
          <Brand />
          <span className="vanir-header__tagline" aria-hidden="true">Hire <i /> Buy <i /> Fleet <i /> Electric</span>
        </Link>
        <div className="vanir-header__actions">
          <a className="vanir-header__call" href="tel:01516731916"><PhoneIcon />Call 0151 673 1916</a>
          <Link className="vanir-header__quote" to="/request-a-quote" onClick={closeNavigation}>Request a quote <span aria-hidden="true">→</span></Link>
        </div>
        {isMobile && <button type="button" ref={menuButton} className="vanir-header__menu-toggle" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} aria-controls="vanir-primary-navigation" onClick={() => {
          setMobileMenuOpen(!mobileMenuOpen);
          setOpenDropdown(null);
        }}>
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">{mobileMenuOpen ? <path d="m6 6 12 12M18 6 6 18" /> : <path d="M4 6h16M4 12h16M4 18h16" />}</svg><span>Menu</span>
        </button>}
        {(!isMobile || mobileMenuOpen) && <nav className={`vanir-header__nav${isMobile ? ' vanir-header__nav--mobile' : ''}`} id="vanir-primary-navigation" aria-label={isMobile ? 'Mobile' : 'Primary'}>
          <ul className="vanir-header__items">
            {navigation.map(({ label, path, children }) => {
              const expanded = openDropdown === path;
              const dropdownId = `vanir-options-${path.slice(1)}`;
              return <li key={path} className="vanir-header__item" onBlur={(event) => {
                if (expanded && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpenDropdown(null);
              }}>
                <div className="vanir-header__item-heading">
                  <NavLink to={path} onClick={closeNavigation}>{label}</NavLink>
                  {children && <button type="button" className="vanir-header__submenu-toggle" aria-label={`${label} options`} aria-expanded={expanded} aria-controls={dropdownId} ref={element => { dropdownButtons.current[path] = element; }} onClick={() => setOpenDropdown(expanded ? null : path)} onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      if (expanded) dropdownLists.current[path]?.querySelector('a')?.focus();
                      else {
                        focusDropdown.current = true;
                        setOpenDropdown(path);
                      }
                    }
                  }}><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m3 6 5 5 5-5" /></svg></button>}
                </div>
                {children && expanded && <ul className="vanir-header__dropdown" id={dropdownId} aria-label={`${label} options`} ref={element => { dropdownLists.current[path] = element; }}>
                  {children.map(child => <li key={child.path}><Link to={child.path} onClick={closeNavigation}>{child.label}</Link></li>)}
                </ul>}
              </li>;
            })}
          </ul>
          {isMobile && <div className="vanir-header__mobile-actions">
            <Link className="vanir-header__quote" to="/request-a-quote" onClick={closeNavigation}>Request a quote <span aria-hidden="true">→</span></Link>
            <a className="vanir-header__call" href="tel:01516731916"><PhoneIcon />Call 0151 673 1916</a>
          </div>}
        </nav>}
      </div>
    </header>
  );
}

export default Header;
